import { useState, useEffect, useRef } from 'react';
import { ArrowRight, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { product as T, IMAGES, lensData, LENS_COLORS } from '../utils/translations';
import { trackBuyDeck, trackGuidedWorkshop } from '../utils/analytics';
import FooterNew from '../components/FooterNew';

const LOGO = 'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/84ltpppp_CultureCracker_Registered.png';
const HANDS_DE = 'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/go3pd79y_B2F7DC05-649B-44A4-AB12-5AED7860FC37.png';
const HANDS_EN = 'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/spdgxl1c_96EEF652-EEAF-4FAA-B47E-7D2B514E5149.png';
const CARD_BACKS_DE = [
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/ull8s1pe_culturecracker_kulturfallen_back.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/8331gsx1_culturecracker_exploration_back.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/64cb73dm_culturecracker_brainstorm_back.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/7r0hqllp_culturecracker_veraenderung_back.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/hro1mvuv_culturecracker_schritt_back.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/goiem8d2_culturecracker_erfolg_back.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/48hjq29r_culturecracker_lighthouse_back.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/ctvtaq5e_culturecracker_stolper_back.png',
];
const CARD_BACKS_EN = [
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/2blxccqv_7B8744A1-79B2-4C49-BD40-36DF27B08A4A.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/fpmpua3f_44A9F3A5-2457-4F8E-A111-AC39729FE724.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/py0lmxsu_BC26E096-2888-4933-B9C5-13D110D66781.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/yx1ic5if_2A947AD9-6679-4F55-802D-600654DBF847.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/l76zpvem_0B9F8AC6-972A-4ECE-995F-10B4C59950AA.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/zckee3z0_0735627E-A456-4ECB-AFDC-8436A14276A8.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/55fe5u6f_35E4EA4C-9111-41D3-BF45-DADFEDD5F460.png',
  'https://customer-assets.emergentagent.com/job_a638eaf5-87e7-49db-ab47-92900872b6eb/artifacts/cvz4xbgf_72C00BF7-8DB2-4B6D-B44D-122F477435CA.png',
];

const useReveal = () => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => { const el = ref.current; if (!el) return; const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.12 }); o.observe(el); return () => o.disconnect(); }, []);
  return [ref, v];
};

const Nav = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = T[language].nav;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { const h = () => setScrolled(window.scrollY > 40); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [open]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${scrolled ? 'glass' : 'bg-transparent'}`} data-testid="header">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex-shrink-0 relative z-[110]"><img src={LOGO} alt="Culture Cracker®" className="h-9 sm:h-12 w-auto" /></Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="eyebrow text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" style={{ letterSpacing: '0.18em', fontSize: 12 }}>{t.back}</Link>
            <button onClick={toggleLanguage} className="eyebrow text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer" style={{ letterSpacing: '0.18em', fontSize: 12 }}>{language === 'de' ? 'EN' : 'DE'}</button>
            <a href="#buy" onClick={() => trackBuyDeck('product_header')} className="btn-primary text-sm py-2.5 px-5" data-testid="header-cta">{t.cta}</a>
          </div>
          <div className="flex md:hidden items-center gap-3">
            <button onClick={toggleLanguage} className="eyebrow text-[var(--text-muted)] text-xs relative z-[110]">{language === 'de' ? 'EN' : 'DE'}</button>
            <button onClick={() => setOpen(!open)} className="p-1.5 text-[var(--text-secondary)] relative z-[110]" aria-label="Menu">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          </div>
        </div>
      </header>
      {open && (
        <div className="fixed inset-0 z-[99] bg-[var(--bg-primary)] flex flex-col pt-20 px-6" style={{ minHeight: '100dvh' }}>
          <nav className="flex flex-col gap-1 mt-8"><Link to="/" onClick={() => setOpen(false)} className="py-4 text-xl text-[var(--text-secondary)] border-b border-[var(--hairline)]">{t.back}</Link></nav>
          <div className="mt-auto mb-12"><a href="#buy" onClick={() => { setOpen(false); trackBuyDeck('product_mobile'); }} className="btn-primary w-full justify-center block text-center py-4">{t.cta}</a></div>
        </div>
      )}
    </>
  );
};

const ProductPage = () => {
  const { language } = useLanguage();
  const t = T[language];
  const lenses = lensData[language];
  const [lensRef, lensVis] = useReveal();
  const [detailRef, detailVis] = useReveal();

  return (
    <>
      <Nav />
      <main className="bg-[var(--bg-primary)] min-h-screen">
        {/* Hero */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="product-hero">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24 w-full">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p className="eyebrow mb-6">{t.hero.eyebrow}</p>
                <h1 className="h1-display mb-6">{t.hero.h1} <em>{t.hero.h1i}</em></h1>
                <p className="body-lg max-w-lg mb-10">{t.hero.sub}</p>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a href="#buy" onClick={() => trackBuyDeck('product')} className="btn-primary" data-testid="product-buy-cta">{t.hero.cta1} <ArrowRight className="w-4 h-4" /></a>
                  <Link to="/#pilot" onClick={() => trackGuidedWorkshop('product')} className="btn-ghost">{t.hero.cta2}</Link>
                </div>
                <p className="font-mono text-[11px] tracking-[0.18em] text-[var(--text-muted)]">{t.hero.note}</p>
              </div>
              <div className="hidden lg:block"><img src={IMAGES[language].boxOpen} alt="Culture Cracker Box" className="w-full max-w-md ml-auto" loading="eager" /></div>
            </div>
          </div>
        </section>

        {/* Lenses */}
        <section ref={lensRef} className="py-24 sm:py-32 lg:py-40 bg-[var(--bg-section)]" data-testid="product-lenses">
          <div className={`max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 reveal ${lensVis?'visible':''}`}>
            <p className="eyebrow mb-6">{t.lenses.eyebrow}</p>
            <h2 className="h2-display max-w-3xl mb-6">{t.lenses.h2} <em>{t.lenses.h2i}</em></h2>
            <p className="body-lg max-w-2xl mb-16">{t.lenses.sub}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {lenses.map((l, i) => (
                <div key={i} className="card-hairline">
                  <div className="w-3 h-3 rounded-full mb-4" style={{ backgroundColor: LENS_COLORS[i] }} />
                  <p className="h3-display text-lg mb-1">{l.name}</p>
                  <p className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase mb-3">{l.subtitle}</p>
                  <p className="body-sm">{l.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Detail */}
        <section ref={detailRef} className="py-24 sm:py-32 lg:py-40 bg-[var(--bg-primary)]">
          <div className={`max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 reveal ${detailVis?'visible':''}`}>
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <img src={language === 'de' ? HANDS_DE : HANDS_EN} alt="Culture Cracker cards" className="w-full rounded-sm order-2 lg:order-1" loading="lazy" />
              <div className="order-1 lg:order-2">
                <p className="eyebrow mb-6">{t.detail.eyebrow}</p>
                <h2 className="h2-display mb-6">{t.detail.h2} <em>{t.detail.h2i}</em></h2>
                <p className="body-lg mb-10">{t.detail.sub}</p>
                <ul className="space-y-4">{t.detail.features.map((f, i) => (<li key={i} className="body-md flex items-start gap-4"><span className="w-1.5 h-1.5 rounded-full bg-[var(--text-primary)] opacity-30 mt-2.5 flex-shrink-0" />{f}</li>))}</ul>
              </div>
            </div>
          </div>
        </section>

        {/* Lens backs row */}
        <section className="py-16 sm:py-24 bg-[var(--bg-section)]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {(language === 'de' ? CARD_BACKS_DE : CARD_BACKS_EN).map((b, i) => (<div key={i} className="transition-transform duration-300 hover:-translate-y-1.5 overflow-hidden" style={{ aspectRatio: '500/842', borderRadius: 14 }}><img src={b} alt={`Lens ${i+1}`} className="w-full h-full object-cover" style={{ borderRadius: 14 }} loading="lazy" /></div>))}
            </div>
          </div>
        </section>

        {/* Buy CTA */}
        <section id="buy" className="py-24 sm:py-32 lg:py-40 bg-[var(--bg-primary)]" data-testid="product-buy-section">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
            <h2 className="h2-display mb-6">{t.buy.h2} <em>{t.buy.h2i}</em></h2>
            <p className="body-lg mb-4">{t.buy.p1}</p>
            <p className="body-sm mb-10">{t.buy.p2}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a href="mailto:hello@culturecracker.io?subject=Culture%20Cracker%20Deck%20Order" onClick={() => trackBuyDeck('product')} className="btn-primary" data-testid="product-bottom-buy-cta">{t.buy.cta1} <ArrowRight className="w-4 h-4" /></a>
              <Link to="/#pilot" onClick={() => trackGuidedWorkshop('product')} className="btn-ghost">{t.buy.cta2}</Link>
            </div>
            <p className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)]">{t.buy.note}</p>
          </div>
        </section>
      </main>
      <FooterNew />
    </>
  );
};

export default ProductPage;
